import React, { useState } from 'react';
import { Ban, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BlockButton = ({ userId, userName, isBlocked, onBlockChange, size = "default", variant = "destructive", showIcon = true }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleBlock = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/block-user`, { user_id: userId }, { withCredentials: true });
      toast.success(`${userName} has been blocked`);
      setShowConfirmDialog(false);
      if (onBlockChange) onBlockChange(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to block user');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/unblock-user`, { user_id: userId }, { withCredentials: true });
      toast.success(`${userName} has been unblocked`);
      if (onBlockChange) onBlockChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unblock user');
    } finally {
      setLoading(false);
    }
  };

  if (isBlocked) {
    return (
      <Button
        onClick={handleUnblock}
        disabled={loading}
        size={size}
        variant="outline"
        className="border-blue-300 text-blue-600 hover:bg-blue-50"
      >
        {showIcon && <Shield className="w-4 h-4 mr-2" />}
        {loading ? 'Unblocking...' : 'Unblock'}
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={() => setShowConfirmDialog(true)}
        size={size}
        variant={variant}
      >
        {showIcon && <Ban className="w-4 h-4 mr-2" />}
        Block
      </Button>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block {userName}?</DialogTitle>
            <DialogDescription>
              <div className="space-y-3 mt-3">
                <p className="text-sm text-gray-700">
                  Blocking this user will:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Remove your match or friendship</li>
                  <li>Delete all messages between you</li>
                  <li>Prevent them from messaging you</li>
                  <li>They will remain visible with "Blocked" status</li>
                </ul>
                <p className="text-xs text-gray-500 mt-2">
                  You can unblock them later, but you'll need to re-match if desired.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex space-x-3 mt-4">
            <Button
              onClick={() => setShowConfirmDialog(false)}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBlock}
              variant="destructive"
              className="flex-1"
              disabled={loading}
            >
              {loading ? 'Blocking...' : 'Block User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BlockButton;
